import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const defaultForm = {
  libelle: "",
  date_debut: "",
  date_fin: "",
};

function Editions() {
  const [editions, setEditions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchEditions = async () => {
    try {
      const res = await api.get("/editions");
      setEditions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEditions();
  }, []);

  const handleOpen = (edition = null) => {
    if (edition) {
      setForm({
        libelle: edition.libelle,
        date_debut: edition.date_debut?.slice(0, 10),
        date_fin: edition.date_fin?.slice(0, 10),
      });
      setEditId(edition.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setError("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setError("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (editId) {
        await api.put(`/editions/${editId}`, form);
      } else {
        await api.post("/editions", form);
      }
      fetchEditions();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    setError("");
    try {
      await api.put(`/editions/${id}/toggle`);
      fetchEditions();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette édition ?")) {
      try {
        await api.delete(`/editions/${id}`);
        fetchEditions();
      } catch (err) {
        alert(err.response?.data?.message || "Erreur serveur");
      }
    }
  };

  const nbActives = editions.filter((e) => e.active).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Éditions
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    {nbActives}/2 édition(s) active(s)
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="outlined"
                  color="white"
                  size="small"
                  onClick={() => handleOpen()}
                  disabled={nbActives >= 2}
                >
                  <Icon>add</Icon>&nbsp;Nouvelle édition
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {editions.map((e) => (
                    <Grid item xs={12} md={6} lg={4} key={e.id}>
                      <Card sx={{
                        p: 2,
                        border: "2px solid",
                        borderColor: e.active ? "#43a047" : "#eee",
                      }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6">{e.libelle}</MDTypography>
                          <Chip
                            label={e.active ? "Active" : "Archivée"}
                            size="small"
                            sx={{
                              bgcolor: e.active ? "#43a047" : "#9e9e9e",
                              color: "white",
                            }}
                          />
                        </MDBox>
                        <MDTypography variant="caption" color="text" display="block">
                          Du : {new Date(e.date_debut).toLocaleDateString("fr-FR")}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Au : {new Date(e.date_fin).toLocaleDateString("fr-FR")}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block" mb={1}>
                          Créée le : {new Date(e.created_at).toLocaleDateString("fr-FR")}
                        </MDTypography>
                        <MDBox display="flex" gap={1} flexWrap="wrap" mt={1}>
                          <MDButton
  size="small"
  color={e.active ? "warning" : "success"}
  variant="outlined"
  onClick={() => handleToggle(e.id)}
>
  <Icon>{e.active ? "archive" : "unarchive"}</Icon>
  &nbsp;{e.active ? "Archiver" : "Activer"}
</MDButton>
                          <MDButton
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={() => handleOpen(e)}
                          >
                            <Icon>edit</Icon>&nbsp;Modifier
                          </MDButton>
                          <MDButton
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleDelete(e.id)}
                          >
                            <Icon>cancel</Icon>
                          </MDButton>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                  {editions.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune édition créée. Commencez par créer une édition.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier l'édition" : "Nouvelle édition"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Libellé (ex: Édition 2025-2026)"
              fullWidth
              value={form.libelle}
              onChange={(e) => setForm({ ...form, libelle: e.target.value })}
            />
            <TextField
              label="Date de début"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.date_debut}
              onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
            />
            <TextField
              label="Date de fin"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.date_fin}
              onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
            />
            {error && (
              <MDTypography variant="caption" color="error">
                {error}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          <MDButton
            onClick={handleSubmit}
            color="info"
            disabled={loading || !form.libelle || !form.date_debut || !form.date_fin}
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Editions;